"use no memo";

import { Button } from "@choose/ui/button";
import {
  Empty,
  EmptyDescription,
  EmptyHeader,
  EmptyMedia,
  EmptyTitle,
} from "@choose/ui/empty";
import { Separator } from "@choose/ui/separator";
import { Spinner } from "@choose/ui/spinner";
import { createFileRoute, Link } from "@tanstack/react-router";
import { ArrowLeftIcon, CandyOffIcon, ServerCrashIcon } from "lucide-react";
import { useTranslation } from "react-i18next";

import { RoleForm } from "~/components/common/settings/members/role-form";
import { useInternalUsersQuery } from "~/graphql/graphql.gen";

export const Route = createFileRoute(
  "/_auth/_app_shell/_settings-navigation/settings/member/$memberId",
)({
  component: RouteComponent,
});

function RouteComponent() {
  const { t } = useTranslation();
  const { memberId } = Route.useParams();
  const {
    data: internalUsers,
    isLoading,
    error,
  } = useInternalUsersQuery(undefined, {
    select: (data) => data.internalUsers,
  });

  const internalUser = internalUsers?.find(
    (internalUser) => internalUser.id === memberId,
  );

  if (isLoading) {
    return (
      <>
        <title>{t("app_name")}</title>

        <div className="bg-background flex size-full flex-col justify-center overflow-auto">
          <div className="bg-background flex size-full flex-col items-center justify-center">
            <Empty className="min-h-[300px] w-full max-w-md">
              <EmptyHeader>
                <EmptyMedia variant="icon">
                  <Spinner className="size-4" />
                </EmptyMedia>
                <EmptyTitle>{t("member.loading.title")}</EmptyTitle>
                <EmptyDescription>
                  {t("member.loading.description")}
                </EmptyDescription>
              </EmptyHeader>
            </Empty>
          </div>
        </div>
      </>
    );
  }

  if (error) {
    return (
      <>
        <title>{t("app_name")}</title>

        <div className="bg-background flex size-full overflow-auto">
          <Empty>
            <EmptyHeader>
              <EmptyMedia variant="icon">
                <ServerCrashIcon />
              </EmptyMedia>
              <EmptyTitle>{t("member.error")}</EmptyTitle>
            </EmptyHeader>
          </Empty>
        </div>
      </>
    );
  }

  if (!internalUser)
    return (
      <>
        <title>{`${t("member.not_found.title")} - ${t("app_name")}`}</title>

        <div className="bg-background flex size-full overflow-auto">
          <Empty>
            <EmptyHeader>
              <EmptyMedia variant="icon">
                <CandyOffIcon />
              </EmptyMedia>
              <EmptyTitle>{t("member.not_found.title")}</EmptyTitle>
              <EmptyDescription>
                {t("member.not_found.description")}
              </EmptyDescription>
            </EmptyHeader>
          </Empty>
        </div>
      </>
    );

  return (
    <>
      <title>{`${t("member.edit_x", { name: `${internalUser.internalUser.firstName} ${internalUser.internalUser.lastName}` })} - ${t("app_name")}`}</title>

      <div className="bg-background w-full overflow-auto">
        <div className="m-auto max-w-2xl p-8">
          <header className="mt-14">
            <Button
              variant="ghost"
              size="sm"
              className="-mx-3 mb-4"
              nativeButton={false}
              render={
                <Link to="/settings/members">
                  <ArrowLeftIcon />
                  <span>{t("settings.member.all_members")}</span>
                </Link>
              }
            />

            <h1 className="text-2xl">{t("settings.members.title")}</h1>
            <span className="text-muted-foreground mt-1 text-sm">
              {t("settings.member.edit_role_of", {
                name: `${internalUser.internalUser.firstName} ${internalUser.internalUser.lastName}`,
              })}
            </span>
          </header>
          <Separator className="my-6" />
          <RoleForm user={internalUser} />
        </div>
      </div>
    </>
  );
}
